import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { ChartAccount, LocaleCode } from '@/api/models';
import type { ErrorResponse } from '@/api/handlers/responses';
import { AccountQueryKeys } from './accountQueryKeys';

/**
 * How long fetched data stays "fresh" before a background refetch. The chart
 * changes rarely, so a generous window avoids needless refetches while the user
 * expands/collapses rows.
 */
const STALE_TIME_MS = 5 * 60_000;

/**
 * The chart is always fetched **with archived accounts included**, and the
 * "Show archived" toggle filters client-side (`buildAccountTree`).
 *
 * Three things need the archived rows even when they aren't drawn: the archive
 * gate ("this group still has children"), the parent picker's cycle exclusion,
 * and reactivation. Refetching on every toggle would also throw away the tree's
 * expansion state for a filter the client can apply for free.
 */
const INCLUDE_ARCHIVED = true;

export type UseChartOfAccountsOptions = {
    /** Locale the server resolves account names into. */
    locale?: LocaleCode;
    /**
     * Skip the request entirely. Used by the page for a session that will be
     * refused anyway (a PIN/kiosk token) — firing a request you know will 403
     * only turns an explainable state into an error state.
     */
    enabled?: boolean;
    /**
     * Balances as of a `yyyy-MM-dd` date. Omitted means live balances — what
     * every screen but the Accounting experiment asks for.
     *
     * A single date, not a range, because an account balance is cumulative:
     * "cash at 31 March" is a complete answer, "cash between January and March"
     * is not. A caller holding a period passes its **end**.
     */
    asOf?: string;
};

export type UseChartOfAccountsResult = {
    /** The chart, balances on the rows; `null` until first load. */
    data: ChartAccount[] | null;
    /**
     * A fresh tenant with no chart at all — route to onboarding. Distinct from
     * an empty `data`, which can also mean "everything was filtered out".
     */
    bootstrapRequired: boolean;
    isLoading: boolean;
    isFetching: boolean;
    error: ErrorResponse | null;
    /** Refetch the chart (drives the error-state Retry button). */
    refetch: () => void;
};

/**
 * Fetches the chart of accounts.
 *
 * One query, not two: `GET /accounts/trial-balance` was dropped when balances
 * moved onto the account row, so the list *is* the balance source. Everything
 * that used to read a trial balance — including the Fixed Assets, Salaries and
 * Dividends register tie-outs — reads this hook.
 *
 * The query function never rejects (it resolves an `ApiResponse`), so failures
 * surface through the response envelope, matching the rest of the client. Only
 * the unwrap lives here — the tree build and balance rollups are pure utils
 * applied downstream so they stay testable (see `buildAccountTree`).
 */
export const useChartOfAccounts = ({
    locale = 'en',
    enabled = true,
    asOf,
}: UseChartOfAccountsOptions = {}): UseChartOfAccountsResult => {
    const { apiClient } = useApi();

    const query = useQuery({
        queryKey: AccountQueryKeys.list(locale, INCLUDE_ARCHIVED, asOf),
        queryFn: () =>
            apiClient.accounts.getAccountList({
                locale,
                includeArchived: INCLUDE_ARCHIVED,
                asOf,
            }),
        staleTime: STALE_TIME_MS,
        gcTime: Infinity,
        enabled,
    });

    const response = query.data;
    const error: ErrorResponse | null =
        response && !response.success ? response.error : null;

    const data: ChartAccount[] | null = React.useMemo(
        () => (response?.success ? response.response.items : null),
        [response],
    );

    const refetch = React.useCallback(() => {
        query.refetch();
    }, [query]);

    return {
        data,
        bootstrapRequired:
            response?.success === true && response.response.bootstrapRequired,
        isLoading: enabled && query.isLoading,
        isFetching: query.isFetching,
        error,
        refetch,
    };
};
